/**
 * Walk-forward evaluation (spec 7.3). Each case predicts from the records
 * known at its basis time only, then scores against the next observed event.
 * The model is compared, on exactly the same cases, with a plain recent-median
 * baseline (unweighted median and 20/80 window of the last N valid samples).
 */
import type { LocalEvent } from '@/lib/schemas';
import { MINUTE_MS, weightedQuantile } from './common';
import { DEFAULT_FEEDING_OPTIONS, computeFeedingPrediction, feedIntervals, feedSessions, type FeedingOptions } from './feeding';
import { DEFAULT_SLEEP_OPTIONS, completedSessions, computeSleepPrediction, type SleepOptions } from './sleep';

export interface Scores {
  cases: number;
  /** Mean absolute error of the point estimate, in minutes (null without cases). */
  maeMin: number | null;
  /** Share of cases whose observation fell inside the window. */
  coverage: number | null;
  meanWidthMin: number | null;
}

export interface Evaluation {
  model: Scores;
  baseline: Scores;
}

interface Case {
  actualMs: number;
  model: { pointMs: number; startMs: number; endMs: number };
  baseline: { pointMs: number; startMs: number; endMs: number };
}

const BASELINE_COUNT = 10;

function recentWindow(basisMs: number, values: number[]) {
  const recent = values.slice(-BASELINE_COUNT).map((value) => ({ value, weight: 1 }));
  return {
    pointMs: basisMs + weightedQuantile(recent, 0.5) * MINUTE_MS,
    startMs: basisMs + weightedQuantile(recent, 0.2) * MINUTE_MS,
    endMs: basisMs + weightedQuantile(recent, 0.8) * MINUTE_MS,
  };
}

function score(cases: Case[], pick: (c: Case) => Case['model']): Scores {
  if (!cases.length) return { cases: 0, maeMin: null, coverage: null, meanWidthMin: null };
  let abs = 0, hits = 0, width = 0;
  for (const c of cases) {
    const w = pick(c);
    abs += Math.abs(c.actualMs - w.pointMs) / MINUTE_MS;
    if (c.actualMs >= w.startMs && c.actualMs <= w.endMs) hits++;
    width += (w.endMs - w.startMs) / MINUTE_MS;
  }
  return { cases: cases.length, maeMin: abs / cases.length, coverage: hits / cases.length, meanWidthMin: width / cases.length };
}

function summarize(cases: Case[]): Evaluation {
  return { model: score(cases, (c) => c.model), baseline: score(cases, (c) => c.baseline) };
}

/** Next-feed backtest. Cases whose true gap exceeds `maxGapMin` are untracked time and skipped. */
export function backtestFeeding(events: LocalEvent[], tz: string, options: Partial<FeedingOptions> = {}): Evaluation {
  const opts = { ...DEFAULT_FEEDING_OPTIONS, ...options };
  const sessions = feedSessions(events.filter((e) => !e.deleted_at), opts.mergeWithinMin);
  const cases: Case[] = [];
  for (let k = 1; k < sessions.length; k++) {
    const basis = sessions[k - 1];
    const actualMs = sessions[k].startMs;
    if ((actualMs - basis.startMs) / MINUTE_MS > opts.maxGapMin) continue;
    const prediction = computeFeedingPrediction(events, tz, basis.startMs, opts);
    if (prediction.status !== 'ok' || prediction.basisEventId !== basis.id) continue;
    const history = feedIntervals(sessions.slice(0, k), tz, opts).map((i) => i.minutes);
    cases.push({ actualMs, model: prediction.window, baseline: recentWindow(basis.startMs, history) });
  }
  return summarize(cases);
}

/** Next-sleep-start backtest from the end of each completed session. */
export function backtestSleepStart(events: LocalEvent[], tz: string, options: Partial<SleepOptions> = {}): Evaluation {
  const opts = { ...DEFAULT_SLEEP_OPTIONS, ...options };
  const sessions = completedSessions(events.filter((e) => !e.deleted_at), opts);
  const cases: Case[] = [];
  for (let i = 1; i < sessions.length; i++) {
    const basisMs = sessions[i - 1].endMs;
    const actualMs = sessions[i].startMs;
    const gapMin = (actualMs - basisMs) / MINUTE_MS;
    if (!(gapMin > 0) || gapMin > opts.maxWakeMin) continue;
    const prediction = computeSleepPrediction(events, tz, basisMs, opts);
    if (prediction.status !== 'awake' || prediction.basisEventId !== sessions[i - 1].id) continue;
    const history: number[] = [];
    for (let j = 1; j < i; j++) {
      const m = (sessions[j].startMs - sessions[j - 1].endMs) / MINUTE_MS;
      if (m > 0 && m <= opts.maxWakeMin && sessions[j].startMs <= basisMs) history.push(m);
    }
    cases.push({ actualMs, model: prediction.window, baseline: recentWindow(basisMs, history) });
  }
  return summarize(cases);
}

/** Wake-time backtest: predicts each session's end from its start, as if it were running. */
export function backtestWake(events: LocalEvent[], tz: string, options: Partial<SleepOptions> = {}): Evaluation {
  const opts = { ...DEFAULT_SLEEP_OPTIONS, ...options };
  const sessions = completedSessions(events.filter((e) => !e.deleted_at), opts);
  const cases: Case[] = [];
  for (const s of sessions) {
    const prediction = computeSleepPrediction(events, tz, s.startMs, opts);
    if (prediction.status !== 'sleeping' || prediction.basisEventId !== s.id) continue;
    const history = sessions.filter((p) => p.endMs <= s.startMs).map((p) => (p.endMs - p.startMs) / MINUTE_MS);
    cases.push({ actualMs: s.endMs, model: prediction.window, baseline: recentWindow(s.startMs, history) });
  }
  return summarize(cases);
}
